import { Component, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { UserManagementServiceService } from '../user-management/user-management-service.service';
import { SnackBarComponent } from 'src/app/shared/snack-bar/snack-bar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from 'src/app/pages/confirmation-dialog/confirmation-dialog.component';
import { CommonServiceService } from 'src/app/pages/services/common-service.service';

@Component({
  selector: 'app-approval-request',
  templateUrl: './approval-request.component.html',
  styleUrls: ['./approval-request.component.scss']
})
export class ApprovalRequestComponent implements OnInit {
  selectedTab: any;
  approvalTabs = [
    { label: 'New Registration Requests', key: 'interest', isChecked: true, isActive: true },
    { label: 'MSME Classification Change Requests', key: 'employeeChange', isChecked: false, isActive: true },
  ];

  // New Registration (Interest) Requests
  interestList: any[] = [];
  interestTotalCount = 0;
  interestPage = 1;
  interestLimit = 10;
  interestSkip = 0;
  interestFilterForm: FormGroup;
  countrys: any[] = [];
  sectorNames: any[] = [];

  // Employee Change (MSME Classification Change Request logs)
  employeeChangeList: any[] = [];
  employeeChangeTotalCount = 0;
  employeeChangePendingCount = 0;
  employeeChangePage = 1;
  employeeChangeLimit = 10;
  employeeChangeSkip = 0;
  employeeChangeFilterForm: FormGroup;
  employeeChangeUsers: any[] = [];
  employeeChangeMsmes: any[] = [];
  msmeClassifications: any[] = [];

  constructor(
    private fb: FormBuilder,
    private userManagementService: UserManagementServiceService,
    private commonService: CommonServiceService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.interestFilterForm = this.fb.group({
      countryId: [''],
      sectorId: [''],
      company_name: [''],
      status: [''],
    });
    this.employeeChangeFilterForm = this.fb.group({
      msme_classification_status: [''], // 1 Pending, 2 Approved, 3 Rejected
      user_id: [''],
      msme_id: [''],
      msme_classification_id: [''],    // current type
      requested_msme_classification_id: [''], // requested type
    });
  }

  ngOnInit(): void {
    this.loadCountryDetails();
    this.loadSectorDetails();
    this.loadEmployeeChangeUsers();
    this.loadEmployeeChangeMsmes();
    this.loadMsmeClassifications();
    this.loadEmployeeChangePendingCount();
    this.selectedTab = this.approvalTabs[0];
    this.tabChanges(this.selectedTab);
    // Preload second tab data so it's ready when user switches
    this.loadEmployeeChangeRequests(1, this.employeeChangeLimit);
  }

  tabChanges(tab: any): void {
    this.approvalTabs.forEach(t => (t.isChecked = false));
    tab.isChecked = true;
    this.selectedTab = tab;
    if (tab.key === 'interest') {
      this.loadInterests(this.interestPage, this.interestLimit);
    } else if (tab.key === 'employeeChange') {
      this.loadEmployeeChangePendingCount();
      this.loadEmployeeChangeRequests(this.employeeChangePage, this.employeeChangeLimit);
    }
  }

  loadCountryDetails(): void {
    this.userManagementService.countryDetails().subscribe((res: any) => {
      if (res?.success) { this.countrys = res.data ?? []; }
    });
  }

  loadSectorDetails(): void {
    this.userManagementService.sectorNameDetails().subscribe((res: any) => {
      if (res?.success) { this.sectorNames = res.data ?? []; }
    });
  }

  onInterestCountryChange(event: MatSelectChange): void {
    const countryId = event?.value?.id ?? event?.value;
    this.sectorNames = [];
    this.interestFilterForm.patchValue({ sectorId: '' });
    if (countryId) {
      this.userManagementService.sectorNameDetails(countryId).subscribe((res: any) => {
        if (res?.success) { this.sectorNames = res.data ?? []; }
      });
    }
  }

  resetInterestFilter(): void {
    this.interestFilterForm.reset();
    this.loadSectorDetails();
    this.interestPage = 1;
    this.loadInterests(1, this.interestLimit);
  }

  applyInterestFilter(): void {
    this.interestPage = 1;
    this.loadInterests(1, this.interestLimit);
  }

  loadEmployeeChangeUsers(): void {
    // this.userManagementService.allUserDetails(500, 1).subscribe((res: any) => {
    //   if (res?.success && res?.data?.length) this.employeeChangeUsers = res.data;
    //   else this.employeeChangeUsers = [];
    // });
    this.commonService.allAdminMenbers('msmes').subscribe((res: any) => {
      if (res?.success){
        this.employeeChangeUsers = res.data;
      } else {
        this.employeeChangeUsers = [];
      }
    }, error => {
      this.employeeChangeUsers = [];
    });
  }

  loadEmployeeChangeMsmes(): void {
    this.commonService.formGetMethod('msme/list', { page: 1, limit: 500 }).subscribe((res: any) => {
      if (res?.success && res?.data?.length) { this.employeeChangeMsmes = res.data; }
      else { this.employeeChangeMsmes = []; }
    });
  }

  loadMsmeClassifications(): void {
    this.commonService.formGetMethod(
      'msme-classification/list', { page: 1, limit: 50, order: 'ASC', key: 'min_employee_count' }
    ).subscribe((res: any) => {
      if (res?.success && res?.data?.length) { this.msmeClassifications = res.data; }
      else { this.msmeClassifications = []; }
    });
  }

  resetEmployeeChangeFilter(): void {
    this.employeeChangeFilterForm.reset();
    this.employeeChangePage = 1;
    this.loadEmployeeChangeRequests(1, this.employeeChangeLimit);
  }

  applyEmployeeChangeFilter(): void {
    this.employeeChangePage = 1;
    this.loadEmployeeChangeRequests(1, this.employeeChangeLimit);
  }

  // ---- New Registration Requests (Interests) ----
  loadInterests(page = 1, limit = 10): void {
    const v = this.interestFilterForm?.value ?? {};
    const companyName = (v.company_name && String(v.company_name).trim()) || '';
    const status = v.status || '';
    const countryId = (v.countryId?.id != null ? v.countryId.id : v.countryId) || '';
    const sectorId = (v.sectorId?.id != null ? v.sectorId.id : v.sectorId) || '';
    this.userManagementService.getInterestsList(page, limit, '', '', companyName, status, countryId, sectorId).subscribe((res: any) => {
      if (res?.success) {
        this.interestList = res?.data ?? [];
        this.interestTotalCount = res?.total ?? 0;
        this.interestPage = page;
        this.interestLimit = limit;
        this.interestSkip = (page - 1) * limit;
      }
    }, (error: any) => {
      this.openSnackBar([error?.message || 'Failed to load requests'], 3000);
    });
  }

  pageHandlerInterest(event: any): void {
    const page = event?.pageIndex != null ? event.pageIndex + 1 : event?.page || 1;
    const limit = event?.pageSize || event?.limit || 10;
    this.loadInterests(page, limit);
  }

  approveInterest(row: any): void {
    if (!row?.id) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'signUp-dialog-class',
      disableClose: true,
      data: {
        message: 'Are you sure you want to approve this registration request?',
        buttonText: { ok: 'Approve', cancel: 'Cancel' },
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userManagementService.approveInterest(String(row.id)).subscribe((res: any) => {
          this.openSnackBar([res?.message || 'Interest approved and MSME created successfully'], 3000);
          this.loadInterests(this.interestPage, this.interestLimit);
        }, (error: any) => {
          this.openSnackBar([error?.message || 'Failed to approve'], 3000);
        });
      }
    });
  }

  rejectInterest(row: any): void {
    if (!row?.id) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '420px',
      panelClass: 'signUp-dialog-class',
      disableClose: true,
      data: {
        message: 'Are you sure you want to reject this registration request? You may add a decline reason below (optional).',
        buttonText: { ok: 'Reject', cancel: 'Cancel' },
        showTextBox: true,
        placeholder: 'Decline reason (optional)',
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.buttonStatus === 'yes') {
        const declineReason = result?.textBox != null ? String(result.textBox).trim() : '';
        this.userManagementService.rejectInterest(
          String(row.id), declineReason ? { decline_reason: declineReason } : {}
        ).subscribe((res: any) => {
          this.openSnackBar([res?.message || 'Interest declined'], 3000);
          this.loadInterests(this.interestPage, this.interestLimit);
        }, (error: any) => {
          this.openSnackBar([error?.message || 'Failed to reject'], 3000);
        });
      }
    });
  }

  // ---- Employee Change Requests (MSME Classification Change logs API) ----
  loadEmployeeChangeRequests(page = 1, limit = 10): void {
    const v = this.employeeChangeFilterForm?.value ?? {};
    const params: any = { page, limit, isMsme: true };
    if (v.user_id) { params.id = v.user_id; }
    if (v.msme_id) { params.msme_id = v.msme_id; }
    const currId = v.msme_classification_id?.id != null ? v.msme_classification_id.id : v.msme_classification_id;
    if (currId) { params.msme_classification_id = currId; }
    const reqId = v.requested_msme_classification_id?.id != null
      ? v.requested_msme_classification_id.id : v.requested_msme_classification_id;
    if (reqId) { params.requested_msme_classification_id = reqId; }
    this.commonService.formGetMethod('user/list', params).subscribe((res: any) => {
      if (res?.success) {
        this.employeeChangeList = res?.data ?? [];
        this.employeeChangeTotalCount = res?.total ?? 0;
        this.employeeChangePage = page;
        this.employeeChangeLimit = limit;
        this.employeeChangeSkip = (page - 1) * limit;
      } else {
        this.employeeChangeList = [];
        this.employeeChangeTotalCount = 0;
        this.employeeChangePendingCount = 0;
      }
    }, (error: any) => {
      this.openSnackBar([error?.message || 'Failed to load employee change requests'], 3000);
      this.employeeChangeList = [];
      this.employeeChangeTotalCount = 0;
      this.employeeChangePendingCount = 0;
    });
  }

  loadEmployeeChangePendingCount(): void {
    this.commonService.formGetMethod(
      'user/list', { page: 1, limit: 1, msme_classification_status: 1, isMsme: true }
    ).subscribe((res: any) => {
      if (res?.success && res?.total != null) { this.employeeChangePendingCount = res.total; }
    });
  }

  pageHandlerEmployeeChange(event: any): void {
    const page = event?.pageIndex != null ? event.pageIndex + 1 : event?.page || 1;
    const limit = event?.pageSize || event?.limit || 10;
    this.loadEmployeeChangeRequests(page, limit);
  }

  getTypeChangeLabel(row: any): string {
    const current = row?.msme_classification?.type ?? (row && row['msme_classification.type']) ?? '—';
    const requestedItem = this.msmeClassifications.find((item: any) => item.id === row?.requested_msme_classification_id);
    const requested = requestedItem?.type ?? '—';
    return `${current} → ${requested}`;
  }

  getEmployeeChangeStatusLabel(row: any): string {
    const s = row?.msme_classification_status;
    if (s === 1) { return 'Pending'; }
    if (s === 2) { return 'Approved'; }
    if (s === 3) { return 'Rejected'; }
    return '—';
  }

  approveMsmeType(row: any): void {
    const userId = row.id;
    if (!userId || row.requestedEmployeeCount == null || !row.requested_msme_classification_id) {
      this.openSnackBar(['No pending MSME type change request found.'], 3000);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'signUp-dialog-class',
      disableClose: true,
      data: {
        message: 'Are you sure you want to approve this employee change request?',
        buttonText: { ok: 'Approve', cancel: 'Cancel' },
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        const payload = {
          name: row.name,
          email: row.email,
          employeeCount: row.requestedEmployeeCount,
          requestedEmployeeCount: row.requestedEmployeeCount,
          msme_classification_status: 2,
        };
        this.userManagementService.updateUser(userId, payload).subscribe(
          (res: any) => {
            if (res?.success) {
              this.openSnackBar(['MSME type change approved successfully.'], 3000);
              this.loadEmployeeChangePendingCount();
              this.loadEmployeeChangeRequests(this.employeeChangePage, this.employeeChangeLimit);
            }
          },
          (error: any) => this.openSnackBar([error?.message || 'Failed to approve.'], 3000)
        );
      }
    });
  }

  rejectMsmeType(row: any): void {
    const userId = row.id;
    if (!userId) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'signUp-dialog-class',
      disableClose: true,
      data: {
        message: 'Are you sure you want to reject this employee change request?',
        buttonText: { ok: 'Reject', cancel: 'Cancel' },
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        const payload = {
          name: row.name,
          email: row.email,
          employeeCount: row.employeeCount,
          requestedEmployeeCount: row.requestedEmployeeCount,
          msme_classification_status: 3,
        };
        this.userManagementService.updateUser(userId, payload).subscribe(
          (res: any) => {
            if (res?.success) {
              this.openSnackBar(['MSME type change rejected.'], 3000);
              this.loadEmployeeChangeRequests(this.employeeChangePage, this.employeeChangeLimit);
            }
          },
          (error: any) => this.openSnackBar([error?.message || 'Failed to reject.'], 3000)
        );
      }
    });
  }

  openSnackBar(data: string[], duration: number): void {
    this.snackBar.openFromComponent(SnackBarComponent, { data, duration });
  }

  formatDisplayDate(value: any): string {
    if (!value) { return '---'; }
    try {
      return formatDate(value, 'dd/MM/yyyy', 'en-GB');
    } catch {
      return '---';
    }
  }
}
